import { useCallback, useRef, useState } from "react";
import { triggerStoryRefresh } from "../services/storyRefreshNow/storyRefreshNowApi";
import { getApiErrorMessage } from "../services/apiErrors";
import type { StoryRefreshTriggerResult } from "../services/storyRefreshNow/storyRefreshNow.types";

interface UseStoryRefreshNowResult {
  /** True from click until the 202 (or failure) lands. Drives the menu-item guard. */
  isRefreshing: boolean;
  /** Last accepted trigger, kept so a caller can show the job id or triggered-at. */
  lastResult: StoryRefreshTriggerResult | null;
  refreshNow: (force?: boolean) => Promise<void>;
}

/**
 * Fires a one-shot "Refresh Now" for a story.
 *
 * Deliberately NOT React Query: there is nothing to cache or invalidate — the
 * endpoint returns a job handle and the spec ships no polling route, so there is
 * no server state for the client to hold. Same plain-hook shape as
 * `useStoryAutoRefresh`, including the `onError` callback so the owning organism
 * decides how to render the message (toast, inline, modal).
 *
 * Success is reported through `onSuccess` rather than returned, because a
 * debounce replay and a fresh trigger both arrive as 202 and the UI treats them
 * identically.
 */
export const useStoryRefreshNow = (
  workspaceId: string,
  storyId: string,
  onError?: (message: string) => void,
  onSuccess?: (result: StoryRefreshTriggerResult) => void,
): UseStoryRefreshNowResult => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastResult, setLastResult] = useState<StoryRefreshTriggerResult | null>(
    null,
  );

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  /**
   * Guards on a ref, not on `isRefreshing`: two clicks inside one React batch
   * would both read the stale `false` from state and fire two POSTs. The server
   * would debounce them, but the second is still a wasted round-trip.
   */
  const inFlightRef = useRef(false);

  const refreshNow = useCallback(
    async (force = false) => {
      if (!workspaceId || !storyId) {
        onErrorRef.current?.("Unable to refresh this story.");
        return;
      }
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      setIsRefreshing(true);
      try {
        const result = await triggerStoryRefresh(workspaceId, storyId, force);
        setLastResult(result);
        onSuccessRef.current?.(result);
      } catch (error) {
        onErrorRef.current?.(
          getApiErrorMessage(error, "Unable to refresh this story."),
        );
      } finally {
        inFlightRef.current = false;
        setIsRefreshing(false);
      }
    },
    [workspaceId, storyId],
  );

  return { isRefreshing, lastResult, refreshNow };
};
